/**
 * Fast Track Education Hub - "The Ascent" Journey Experience
 *
 * Handles:
 * - Step navigation and progress tracking
 * - Accordion interactions
 * - PDF viewer integration
 * - Cheat sheet vault loading
 * - Progress persistence (localStorage)
 * - Toast notifications
 */

(function() {
  'use strict';

  // ========================================
  // STATE MANAGEMENT
  // ========================================

  const STATE_KEY = 'ft_hub_progress';

  const state = {
    currentStep: 1,
    completedSteps: [],
    stepProgress: {
      1: { resources: [], completed: false },
      2: { resources: [], completed: false },
      3: { resources: [], completed: false },
      4: { resources: [], completed: false },
      5: { resources: [], completed: false }
    },
    cheatSheets: [],
    isInitialized: false
  };

  // Load saved state from localStorage
  function loadState() {
    try {
      const saved = localStorage.getItem(STATE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        Object.assign(state, parsed);
      }
    } catch (e) {
      console.warn('Could not load saved progress:', e);
    }
  }

  // Save state to localStorage
  function saveState() {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify({
        currentStep: state.currentStep,
        completedSteps: state.completedSteps,
        stepProgress: state.stepProgress
      }));
    } catch (e) {
      console.warn('Could not save progress:', e);
    }
  }

  // ========================================
  // DOM ELEMENTS
  // ========================================

  const elements = {
    hub: null,
    stepPanels: [],
    stepperNodes: [],
    stepperProgress: null,
    quickjumpPills: [],
    stepChecklist: [],
    mobileNavBtns: [],
    progressRing: null,
    progressPercent: null,
    headerProgressFill: null,
    headerProgressPercent: null,
    mobileProgressFill: null,
    mobileProgressPercent: null,
    currentStepNum: null,
    mobileStepNum: null,
    upNextCard: null,
    upNextTitle: null,
    upNextDesc: null,
    goToNextStepBtn: null,
    welcomeMessage: null,
    startJourneyBtn: null,
    pdfModal: null,
    pdfModalBackdrop: null,
    pdfModalClose: null,
    pdfModalTitle: null,
    pdfModalFrame: null,
    pdfModalLoading: null,
    toast: null,
    toastTitle: null,
    toastMessage: null,
    cheatsheetGrid: null,
    cheatsheetSearch: null
  };

  function cacheElements() {
    elements.hub = document.getElementById('fasttrackHub');
    if (!elements.hub) return false;

    elements.stepPanels = Array.from(document.querySelectorAll('.ft-step-panel'));
    elements.stepperNodes = Array.from(document.querySelectorAll('.ft-stepper__node'));
    elements.stepperProgress = document.getElementById('stepperProgress');
    elements.quickjumpPills = Array.from(document.querySelectorAll('.ft-quickjump-pill'));
    elements.stepChecklist = Array.from(document.querySelectorAll('.ft-step-check'));
    elements.mobileNavBtns = Array.from(document.querySelectorAll('.ft-mobile-nav__btn'));
    elements.progressRing = document.getElementById('sidebarProgressRing');
    elements.progressPercent = document.getElementById('sidebarProgressPercent');
    elements.headerProgressFill = document.getElementById('headerProgressFill');
    elements.headerProgressPercent = document.getElementById('headerProgressPercent');
    elements.mobileProgressFill = document.getElementById('mobileProgressFill');
    elements.mobileProgressPercent = document.getElementById('mobileProgressPercent');
    elements.currentStepNum = document.getElementById('currentStepNum');
    elements.mobileStepNum = document.getElementById('mobileStepNum');
    elements.upNextCard = document.getElementById('upNextCard');
    elements.upNextTitle = document.getElementById('upNextTitle');
    elements.upNextDesc = document.getElementById('upNextDesc');
    elements.goToNextStepBtn = document.getElementById('goToNextStepBtn');
    elements.welcomeMessage = document.getElementById('welcomeMessage');
    elements.startJourneyBtn = document.getElementById('startJourneyBtn');
    elements.pdfModal = document.getElementById('pdfModal');
    elements.pdfModalBackdrop = document.getElementById('pdfModalBackdrop');
    elements.pdfModalClose = document.getElementById('pdfModalClose');
    elements.pdfModalTitle = document.getElementById('pdfModalTitle');
    elements.pdfModalFrame = document.getElementById('pdfModalFrame');
    elements.pdfModalLoading = document.getElementById('pdfModalLoading');
    elements.toast = document.getElementById('toast');
    elements.toastTitle = document.getElementById('toastTitle');
    elements.toastMessage = document.getElementById('toastMessage');
    elements.cheatsheetGrid = document.getElementById('cheatsheetGrid');
    elements.cheatsheetSearch = document.getElementById('cheatsheetSearch');

    return true;
  }

  // ========================================
  // STEP NAVIGATION
  // ========================================

  function goToStep(stepNum) {
    if (stepNum < 1 || stepNum > 5) return;

    state.currentStep = stepNum;

    // Update step panels
    elements.stepPanels.forEach((panel, index) => {
      const isActive = index + 1 === stepNum;
      panel.classList.toggle('ft-step-panel--active', isActive);
      panel.hidden = !isActive;
    });

    // Update stepper nodes
    elements.stepperNodes.forEach((node, index) => {
      const step = index + 1;
      const isCompleted = state.completedSteps.includes(step);
      const isActive = step === stepNum;

      node.classList.toggle('ft-stepper__node--active', isActive);
      node.classList.toggle('ft-stepper__node--completed', isCompleted);
      node.setAttribute('aria-current', isActive ? 'step' : 'false');
    });

    // Update quickjump pills
    elements.quickjumpPills.forEach((pill) => {
      const step = parseInt(pill.dataset.step, 10);
      pill.classList.toggle('ft-quickjump-pill--active', step === stepNum);
    });

    // Update step checklist
    elements.stepChecklist.forEach((check) => {
      const step = parseInt(check.dataset.step, 10);
      const isCompleted = state.completedSteps.includes(step);
      const isCurrent = step === stepNum;

      check.classList.toggle('ft-step-check--completed', isCompleted);
      check.classList.toggle('ft-step-check--current', isCurrent && !isCompleted);
    });

    // Update mobile nav
    elements.mobileNavBtns.forEach((btn) => {
      const step = parseInt(btn.dataset.step, 10);
      const isCompleted = state.completedSteps.includes(step);
      const isActive = step === stepNum;

      btn.classList.toggle('ft-mobile-nav__btn--active', isActive);
      btn.classList.toggle('ft-mobile-nav__btn--completed', isCompleted);
    });

    // Update step number displays
    if (elements.currentStepNum) elements.currentStepNum.textContent = stepNum;
    if (elements.mobileStepNum) elements.mobileStepNum.textContent = stepNum;

    // Update progress displays
    updateProgress();

    // Update "Up Next" card
    updateUpNextCard(stepNum);

    // Update stepper track progress
    updateStepperTrack();

    // Save state
    saveState();

    // Scroll to top of content
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function updateStepperTrack() {
    if (!elements.stepperProgress) return;

    // Calculate progress based on completed steps
    const completedCount = state.completedSteps.length;
    const progressPercent = (completedCount / 5) * 100;

    elements.stepperProgress.style.width = `${progressPercent}%`;
  }

  function updateProgress() {
    const completedCount = state.completedSteps.length;
    const progressPercent = Math.round((completedCount / 5) * 100);

    // Update progress ring (stroke-dashoffset calculation)
    if (elements.progressRing) {
      const circumference = 327; // 2 * PI * 52
      const offset = circumference - (progressPercent / 100) * circumference;
      elements.progressRing.style.strokeDashoffset = offset;
    }

    // Update percentage displays
    if (elements.progressPercent) elements.progressPercent.textContent = `${progressPercent}%`;
    if (elements.headerProgressPercent) elements.headerProgressPercent.textContent = `${progressPercent}%`;
    if (elements.mobileProgressPercent) elements.mobileProgressPercent.textContent = `${progressPercent}%`;

    // Update progress bars
    if (elements.headerProgressFill) elements.headerProgressFill.style.width = `${progressPercent}%`;
    if (elements.mobileProgressFill) elements.mobileProgressFill.style.width = `${progressPercent}%`;
  }

  function updateUpNextCard(currentStep) {
    if (!elements.upNextCard || !window.FT_HUB_CONFIG) return;

    const nextStep = currentStep + 1;

    if (nextStep > 5) {
      // All steps completed
      elements.upNextCard.style.display = 'none';
      return;
    }

    elements.upNextCard.style.display = 'block';

    const nextStepConfig = window.FT_HUB_CONFIG.steps[nextStep - 1];
    if (nextStepConfig) {
      if (elements.upNextTitle) elements.upNextTitle.textContent = nextStepConfig.title;

      // Set description based on step
      const descriptions = {
        2: 'Build your knowledge foundation with video lectures and books.',
        3: 'Test your knowledge with 3,000+ practice questions.',
        4: 'Reinforce key concepts with crash courses and condensed review books.',
        5: 'Final preparation tools to ensure exam readiness.'
      };

      if (elements.upNextDesc) {
        elements.upNextDesc.textContent = descriptions[nextStep] || '';
      }
    }
  }

  function markStepComplete(stepNum) {
    if (!state.completedSteps.includes(stepNum)) {
      state.completedSteps.push(stepNum);
      state.stepProgress[stepNum].completed = true;
      saveState();

      // Update UI
      goToStep(state.currentStep);

      // Show celebration toast
      showToast('Step Completed!', `You've completed Step ${stepNum}`);
    }
  }

  // ========================================
  // ACCORDION BEHAVIOR
  // ========================================

  function initAccordions() {
    const accordions = document.querySelectorAll('.ft-accordion');

    accordions.forEach(accordion => {
      accordion.addEventListener('toggle', (e) => {
        if (accordion.open) {
          // Track that this resource was opened
          const resourceType = accordion.dataset.resourceType;
          if (resourceType) {
            trackResourceOpened(resourceType);
          }

          // Load cheat sheets if this is the cheat sheet accordion
          if (resourceType === 'cheat-sheets' && state.cheatSheets.length === 0) {
            loadCheatSheets();
          }
        }
      });
    });
  }

  function trackResourceOpened(resourceType) {
    const stepNum = state.currentStep;

    if (!state.stepProgress[stepNum].resources.includes(resourceType)) {
      state.stepProgress[stepNum].resources.push(resourceType);
      saveState();
    }
  }

  // ========================================
  // CHEAT SHEET VAULT
  // ========================================

  async function loadCheatSheets() {
    if (!elements.cheatsheetGrid) return;

    // Show loading state
    elements.cheatsheetGrid.innerHTML = `
      <div class="ft-cheatsheet-loading">
        <div class="ft-spinner"></div>
        <span>Loading cheat sheets...</span>
      </div>
    `;

    try {
      // For now, we'll use a static list since we don't have direct Supabase storage listing
      // In production, this would fetch from Supabase storage API
      const cheatSheets = await fetchCheatSheetList();
      state.cheatSheets = cheatSheets;
      renderCheatSheets(cheatSheets);
    } catch (error) {
      console.error('Error loading cheat sheets:', error);
      elements.cheatsheetGrid.innerHTML = `
        <div class="ft-cheatsheet-loading">
          <span>Could not load cheat sheets. Please try again.</span>
        </div>
      `;
    }
  }

  async function fetchCheatSheetList() {
    // Static list of cheat sheets matching actual filenames in Supabase storage
    // File names must match exactly what's in the cheat-sheets bucket
    return [
      { name: 'Acute & Concerning Skin Conditions', file: 'Acute & Concerning Skin Conditions Cheat Sheet.pdf' },
      { name: 'Acute Kidney Injury & CKD', file: 'Acute Kidney Injury & Chronic Kidney Disease Cheat Sheet.pdf' },
      { name: 'ADHD & Autism', file: 'ADHD & Autism Cheat Sheet.pdf' },
      { name: 'Analgesics', file: 'Analgesics Cheat Sheet.pdf' },
      { name: 'Antepartum Assessment', file: 'Antepartum Assessment & Discomforts of Pregnancy Cheat Sheet.pdf' },
      { name: 'Antibiotics', file: 'Antibiotics Cheat Sheet.pdf' },
      { name: 'Anticoagulants & Antiplatelets', file: 'Anticoagulants & Antiplatelets Cheat Sheet.pdf' },
      { name: 'Antidepressants', file: 'Antidepressants Cheat Sheet.pdf' },
      { name: 'Antiepileptic Medications', file: 'Antiepileptic Medications Cheat Sheet.pdf' },
      { name: 'Antipsychotics', file: 'Antipsychotics Cheat Sheet.pdf' },
      { name: 'Anxiety & OCD', file: 'Anxiety & OCD Cheat Sheet.pdf' },
      { name: 'Anxiolytics & Sedative-Hypnotics', file: 'Anxiolytics & Sedative-Hypnotics Cheat Sheet.pdf' },
      { name: 'Asthma', file: 'Asthma Cheat Sheet.pdf' },
      { name: 'Bipolar Disorders', file: 'Bipolar Disorders Cheat Sheet.pdf' },
      { name: 'Burns & Inhalation Injury', file: 'Burns & Inhalation Injury Cheat Sheet.pdf' },
      { name: 'Cancer', file: 'Cancer Cheat Sheet.pdf' },
      { name: 'Cholecystitis & Pancreatitis', file: 'Cholecystitis & Pancreatitis Cheat Sheet.pdf' },
      { name: 'Chorioamnionitis & Neonatal Sepsis', file: 'Chorioamnionitis & Neonatal Sepsis Cheat Sheet.pdf' },
      { name: 'Cleft Palate & TEF', file: 'Cleft Palate, Tracheoesophageal Fistula, & Reflux Cheat Sheet.pdf' },
      { name: 'Congenital Heart Defects', file: 'Congenital Heart Defects Cheat Sheet.pdf' },
      { name: 'COPD', file: 'COPD Cheat Sheet.pdf' },
      { name: 'Corticosteroids', file: 'Corticosteroids Cheat Sheet.pdf' },
      { name: 'Croup, Epiglottitis & Tonsillitis', file: 'Croup, Epiglottitis, & Tonsillitis Cheat Sheet.pdf' },
      { name: 'Cystic Fibrosis', file: 'Cystic Fibrosis Cheat Sheet.pdf' },
      { name: 'Delegation & Assignment', file: 'Delegation & Assignment Cheat Sheet.pdf' },
      { name: 'Depression & Suicide', file: 'Depression & Suicide Cheat Sheet.pdf' },
      { name: 'Diabetes Mellitus', file: 'Diabetes Mellitus Cheat Sheet.pdf' },
      { name: 'Diuretics', file: 'Diuretics Cheat Sheet.pdf' },
      { name: 'DVT & PE', file: 'DVT & PE Cheat Sheet.pdf' },
      { name: 'Eating Disorders', file: 'Eating Disorders Cheat Sheet.pdf' },
      { name: 'Ectopic & Molar Pregnancy', file: 'Ectopic & Molar Pregnancy Cheat Sheet.pdf' },
      { name: 'Endocarditis & Pericarditis', file: 'Endocarditis, Pericarditis, & Cardiac Tamponade Cheat Sheet.pdf' },
      { name: 'Ethics', file: 'Ethics Cheat Sheet.pdf' },
      { name: 'Fetal Monitoring', file: 'Fetal Monitoring Cheat Sheet.pdf' },
      { name: 'Fluid & Electrolyte Imbalances', file: 'Fluid & Electrolyte Imbalances Cheat Sheet.pdf' },
      { name: 'Fractures', file: 'Fractures Cheat Sheet.pdf' },
      { name: 'GERD & Peptic Ulcer Disease', file: 'GERD, Peptic Ulcer Disease, & GI Bleeding Cheat Sheet.pdf' },
      { name: 'Gestational Diabetes', file: 'Gestational Diabetes Cheat Sheet.pdf' },
      { name: 'Growth & Development', file: 'Growth & Development Cheat Sheet.pdf' },
      { name: 'Hearing Loss & Vertigo', file: 'Hearing Loss & Vertigo Cheat Sheet.pdf' },
      { name: 'Heart Failure', file: 'Heart Failure Cheat Sheet.pdf' },
      { name: 'Hemophilia', file: 'Hemophilia Cheat Sheet.pdf' },
      { name: 'HIV & AIDS', file: 'HIV & AIDS Cheat Sheet.pdf' },
      { name: 'Hyperbilirubinemia', file: 'Hyperbilirubinemia & Hemolytic Disease of the Newborn Cheat Sheet.pdf' },
      { name: 'Hypertension', file: 'Hypertension Cheat Sheet.pdf' },
      { name: 'Infection Control', file: 'Infection Control Cheat Sheet.pdf' },
      { name: 'Infectious Diseases of Childhood', file: 'Infectious Diseases of Childhood Cheat Sheet.pdf' },
      { name: 'Labor Emergencies', file: 'Labor Emergencies Cheat Sheet.pdf' },
      { name: 'Legal', file: 'Legal Cheat Sheet.pdf' },
      { name: 'Management Principles', file: 'Management Principles Cheat Sheet.pdf' },
      { name: 'Mobility and Positioning', file: 'Mobility and Positioning Cheat Sheet.pdf' },
      { name: 'Neural Tube Defects & Hydrocephalus', file: 'Neural Tube Defects & Hydrocephalus Cheat Sheet.pdf' },
      { name: 'Neuromuscular Disorders', file: 'Neuromuscular Disorders Cheat Sheet.pdf' },
      { name: 'Newborn Care', file: 'Newborn Care Cheat Sheet.pdf' },
      { name: 'Otitis Media & Conjunctivitis', file: 'Otitis Media, Conjunctivitis, & Strabismus Cheat Sheet.pdf' },
      { name: 'Parkinson Disease & ALS', file: 'Parkinson Disease & ALS Cheat Sheet.pdf' },
      { name: 'Perioperative Care', file: 'Perioperative Care Cheat Sheet.pdf' },
      { name: 'Placenta Previa & Abruption', file: 'Placenta Previa & Placental Abruption Cheat Sheet.pdf' },
      { name: 'Pneumothorax & Chest Tubes', file: 'Pneumothorax & Chest Tube Management Cheat Sheet.pdf' },
      { name: 'Postpartum Care', file: 'Postpartum Care Cheat Sheet.pdf' },
      { name: 'Postpartum Hemorrhage', file: 'Postpartum Hemorrhage Cheat Sheet.pdf' },
      { name: 'Preeclampsia', file: 'Preeclampsia Cheat Sheet.pdf' },
      { name: 'Preterm Labor & Delivery', file: 'Preterm Labor & Delivery Cheat Sheet.pdf' },
      { name: 'Prioritization', file: 'Prioritization Cheat Sheet.pdf' },
      { name: 'Process of Labor & OB Procedures', file: 'Process of Labor & OB Procedures Cheat Sheet.pdf' },
      { name: 'Pyloric Stenosis & Intussusception', file: 'Pyloric Stenosis, Intussusception, & Hirschsprung Disease Cheat Sheet.pdf' },
      { name: 'Rheumatic Fever & Kawasaki Disease', file: 'Rheumatic Fever & Kawasaki Disease Cheat Sheet.pdf' },
      { name: 'RSV and Bronchiolitis', file: 'RSV and Bronchiolitis Cheat Sheet.pdf' },
      { name: 'Safety', file: 'Safety Cheat Sheet.pdf' },
      { name: 'Schizophrenia', file: 'Schizophrenia Cheat Sheet.pdf' },
      { name: 'Seizures', file: 'Seizures Cheat Sheet.pdf' },
      { name: 'Shock & Sepsis', file: 'Shock & Sepsis Cheat Sheet.pdf' },
      { name: 'Sickle Cell Disease', file: 'Sickle Cell Disease Cheat Sheet.pdf' },
      { name: 'Stroke', file: 'Stroke Cheat Sheet.pdf' },
      { name: 'Therapeutic Communication', file: 'Therapeutic Communication Cheat Sheet.pdf' },
      { name: 'Thyroid & Parathyroid Disorders', file: 'Thyroid & Parathyroid Disorders Cheat Sheet.pdf' },
      { name: 'TORCH Infections', file: 'TORCH Infections Cheat Sheet.pdf' },
      { name: 'Urinary Tract Infections', file: 'Urinary Tract Infections Cheat Sheet.pdf' },
      { name: 'Vascular Disorders', file: 'Vascular Disorders Cheat Sheet.pdf' }
    ];
  }

  function renderCheatSheets(sheets) {
    if (!elements.cheatsheetGrid) return;

    elements.cheatsheetGrid.innerHTML = sheets.map(sheet => `
      <div class="ft-cheatsheet-card" data-file="${sheet.file}" data-name="${sheet.name.toLowerCase()}">
        <div class="ft-cheatsheet-card__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
        </div>
        <span class="ft-cheatsheet-card__name">${sheet.name}</span>
      </div>
    `).join('');

    // Add click handlers
    elements.cheatsheetGrid.querySelectorAll('.ft-cheatsheet-card').forEach(card => {
      card.addEventListener('click', () => {
        const file = card.dataset.file;
        const name = card.querySelector('.ft-cheatsheet-card__name').textContent;
        openCheatSheet(file, name);
      });
    });
  }

  function filterCheatSheets(query) {
    if (!elements.cheatsheetGrid) return;

    const normalizedQuery = query.toLowerCase().trim();
    const cards = elements.cheatsheetGrid.querySelectorAll('.ft-cheatsheet-card');

    cards.forEach(card => {
      const name = card.dataset.name;
      const matches = !normalizedQuery || name.includes(normalizedQuery);
      card.style.display = matches ? '' : 'none';
    });
  }

  async function openCheatSheet(file, name) {
    const config = window.FT_HUB_CONFIG;
    if (!config) return;

    // Show loading state in modal immediately
    if (elements.pdfModal) {
      if (elements.pdfModalTitle) {
        elements.pdfModalTitle.textContent = name;
      }
      if (elements.pdfModalLoading) {
        elements.pdfModalLoading.style.display = 'flex';
      }
      if (elements.pdfModalFrame) {
        elements.pdfModalFrame.src = '';
      }
      elements.pdfModal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    try {
      const supabase = window.YNS_SUPABASE;
      if (!supabase || !supabase.config) {
        throw new Error('Supabase client not initialized');
      }

      // Call get-pdf-url edge function with type: 'cheatsheet'
      const response = await fetch(
        `${supabase.config.url}/functions/v1/get-pdf-url`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            type: 'cheatsheet',
            filename: file
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to get cheat sheet URL: ${response.status}`);
      }

      const data = await response.json();

      if (!data.url) {
        throw new Error('No URL in response');
      }

      // Open PDF in modal
      if (elements.pdfModalFrame) {
        elements.pdfModalFrame.src = data.url;
        elements.pdfModalFrame.onload = () => {
          if (elements.pdfModalLoading) {
            elements.pdfModalLoading.style.display = 'none';
          }
        };
      }
    } catch (error) {
      console.error('Error opening cheat sheet:', error);
      closePdfModal();
      showToast('Error', 'Could not open cheat sheet. Please try again.');
    }
  }

  // ========================================
  // PDF VIEWER
  // ========================================

  function initPdfButtons() {
    // Handle all PDF open buttons
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('[data-action="open-pdf"]');
      if (!btn) return;

      const pdfKey = btn.dataset.pdfKey;
      if (!pdfKey) return;

      await openPdfByKey(pdfKey);
    });
  }

  async function openPdfByKey(pdfKey) {
    const config = window.FT_HUB_CONFIG;
    if (!config || !config.pdfs) return;

    const pdfId = config.pdfs[pdfKey];
    if (!pdfId) {
      showToast('Error', 'PDF not available');
      return;
    }

    // Get signed URL from API
    try {
      const signedUrl = await getSignedPdfUrl(pdfId);
      if (signedUrl) {
        // Get title based on key
        const titles = {
          planner: '3-8 Week Study Planner',
          blueprint: 'Personalized Study Blueprint',
          nclexIntro: 'What Is NCLEX?',
          yellowBook: 'Yellow Book - Lecture Notes',
          blueBook: 'Blue Book - Quiz & Practice',
          crashNotes: '45-Minute Crash Course Book',
          masteryNotes: 'Ultimate Mastery Book',
          finalChecklist: 'Final Week Checklist',
          selfAssessment: 'I\'m Ready Self-Assessment'
        };

        openPdfModal(signedUrl, titles[pdfKey] || 'Document');
      }
    } catch (error) {
      console.error('Error opening PDF:', error);
      showToast('Error', 'Could not open PDF');
    }
  }

  async function getSignedPdfUrl(pdfId) {
    // Use the existing PDF token system from hub-pdf-data.liquid
    if (!window.YNS_PDF_TOKEN || !window.YNS_PDF_API_URL) {
      console.error('PDF token not available');
      return null;
    }

    try {
      // Combine payload + signature (must match edge function expectation)
      const tokenString = window.YNS_PDF_TOKEN.payload + '.' + window.YNS_PDF_TOKEN.signature;

      const response = await fetch(window.YNS_PDF_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pdfId: pdfId,
          token: tokenString  // Combined token like yns-pdf-library.js does
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get signed URL');
      }

      const data = await response.json();
      return data.url;  // Edge function returns 'url', not 'signedUrl'
    } catch (error) {
      console.error('Error getting signed PDF URL:', error);
      return null;
    }
  }

  function openPdfModal(url, title) {
    if (!elements.pdfModal) return;

    // Set title
    if (elements.pdfModalTitle) {
      elements.pdfModalTitle.textContent = title;
    }

    // Show loading
    if (elements.pdfModalLoading) {
      elements.pdfModalLoading.style.display = 'flex';
    }

    // Set iframe src
    if (elements.pdfModalFrame) {
      elements.pdfModalFrame.src = url;
      elements.pdfModalFrame.onload = () => {
        if (elements.pdfModalLoading) {
          elements.pdfModalLoading.style.display = 'none';
        }
      };
    }

    // Show modal
    elements.pdfModal.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  function closePdfModal() {
    if (!elements.pdfModal) return;

    elements.pdfModal.hidden = true;
    document.body.style.overflow = '';

    // Clear iframe
    if (elements.pdfModalFrame) {
      elements.pdfModalFrame.src = '';
    }
  }

  // ========================================
  // TOAST NOTIFICATIONS
  // ========================================

  let toastTimeout = null;

  function showToast(title, message, duration = 3000) {
    if (!elements.toast) return;

    // Clear existing timeout
    if (toastTimeout) {
      clearTimeout(toastTimeout);
    }

    // Set content
    if (elements.toastTitle) elements.toastTitle.textContent = title;
    if (elements.toastMessage) elements.toastMessage.textContent = message;

    // Show toast
    elements.toast.hidden = false;

    // Auto-hide
    toastTimeout = setTimeout(() => {
      elements.toast.hidden = true;
    }, duration);
  }

  // ========================================
  // EVENT LISTENERS
  // ========================================

  function initEventListeners() {
    // Stepper node clicks
    elements.stepperNodes.forEach(node => {
      node.addEventListener('click', () => {
        const step = parseInt(node.dataset.step, 10);
        goToStep(step);
      });
    });

    // Quickjump pill clicks
    elements.quickjumpPills.forEach(pill => {
      pill.addEventListener('click', () => {
        const step = parseInt(pill.dataset.step, 10);
        goToStep(step);
      });
    });

    // Mobile nav clicks
    elements.mobileNavBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.dataset.step, 10);
        goToStep(step);
      });
    });

    // Go to next step button
    if (elements.goToNextStepBtn) {
      elements.goToNextStepBtn.addEventListener('click', () => {
        const nextStep = state.currentStep + 1;
        if (nextStep <= 5) {
          goToStep(nextStep);
        }
      });
    }

    // Welcome message start button
    if (elements.startJourneyBtn) {
      elements.startJourneyBtn.addEventListener('click', () => {
        if (elements.welcomeMessage) {
          elements.welcomeMessage.style.display = 'none';
        }
        goToStep(1);

        // Mark as seen
        localStorage.setItem('ft_hub_welcomed', 'true');
      });
    }

    // PDF modal close
    if (elements.pdfModalClose) {
      elements.pdfModalClose.addEventListener('click', closePdfModal);
    }
    if (elements.pdfModalBackdrop) {
      elements.pdfModalBackdrop.addEventListener('click', closePdfModal);
    }

    // Escape key closes modal
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && elements.pdfModal && !elements.pdfModal.hidden) {
        closePdfModal();
      }
    });

    // Cheat sheet search
    if (elements.cheatsheetSearch) {
      elements.cheatsheetSearch.addEventListener('input', (e) => {
        filterCheatSheets(e.target.value);
      });
    }

    // Step completion tracking
    // Mark step complete when all accordions in that step have been opened
    elements.stepPanels.forEach((panel, index) => {
      const stepNum = index + 1;
      const accordions = panel.querySelectorAll('.ft-accordion');

      accordions.forEach(accordion => {
        accordion.addEventListener('toggle', () => {
          if (accordion.open) {
            checkStepCompletion(stepNum);
          }
        });
      });
    });
  }

  function checkStepCompletion(stepNum) {
    const panel = document.querySelector(`#stepPanel${stepNum}`);
    if (!panel) return;

    const accordions = panel.querySelectorAll('.ft-accordion');
    const openedAccordions = panel.querySelectorAll('.ft-accordion[open]');

    // If all accordions have been opened at least once, mark step as complete
    // For now, just opening one accordion per step counts as progress
    if (openedAccordions.length >= 1) {
      // Auto-mark step complete after viewing content
      setTimeout(() => {
        if (state.stepProgress[stepNum].resources.length > 0) {
          markStepComplete(stepNum);
        }
      }, 2000);
    }
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  function init() {
    if (!cacheElements()) {
      console.warn('Fast Track Hub: Required elements not found');
      return;
    }

    // Load saved state
    loadState();

    // Check if first visit
    const hasWelcomed = localStorage.getItem('ft_hub_welcomed');
    if (!hasWelcomed && elements.welcomeMessage) {
      elements.welcomeMessage.style.display = 'block';
    }

    // Initialize components
    initAccordions();
    initPdfButtons();
    initEventListeners();

    // Set initial step
    goToStep(state.currentStep);

    state.isInitialized = true;

  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API for external use
  window.FTHub = {
    goToStep,
    markStepComplete,
    showToast,
    openPdfByKey,
    getState: () => ({ ...state })
  };

})();
